import { queryOptions, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listResource, getResource, createResource, updateResource, apiFetch } from '@diggens/api-client';
import type { Submission } from '@diggens/types';

export function useSubmissions(portfolioCompanyId?: string) {
  return useQuery({
    queryKey: ['submissions', portfolioCompanyId],
    queryFn: async () => {
      const params: Record<string, string | undefined> = { portfolio_company_id: portfolioCompanyId };
      const { data } = await listResource<Submission>('submissions', params);
      return data;
    },
  });
}

export function useSubmission(submissionId: string) {
  return useQuery({
    queryKey: ['submissions', submissionId],
    queryFn: () => getResource<Submission>('submissions', submissionId),
    enabled: !!submissionId,
  });
}

export function useCreateSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { portfolio_company_id: string; period: string; source_type: 'upload' | 'manual'; tenant_id?: string }) =>
      createResource<Submission>('submissions', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
    },
  });
}

export function useUpdateSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ submissionId, updates }: { submissionId: string; updates: Partial<Submission> }) =>
      updateResource<Submission>('submissions', submissionId, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      queryClient.invalidateQueries({
        queryKey: ['submissions', variables.submissionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['submission-details', variables.submissionId],
      });
    },
  });
}

export interface FundSubmissionStatus {
  company_id: string;
  company_name: string;
  submission_id: string | null;
  status: 'draft' | 'submitted' | 'processing' | 'completed' | 'needs_review' | null;
  submitted_at: string | null;
  metrics_count: number;
  metrics?: any[];
  portfolio_companies?: { id: string; name: string };
  period?: string;
  created_at?: string;
  last_email_sent_at?: string | null;
  email_status?: string | null;
  reminder_count?: number;
}

/** Each portfolio company of a fund with its submission for a period; shared so pages reading several funds use the same cache. */
export function fundSubmissionsQuery(fundId: string, period: string) {
  return queryOptions({
    queryKey: ['fund-submissions', fundId, period],
    queryFn: () => apiFetch<FundSubmissionStatus[]>(`/funds/${encodeURIComponent(fundId)}/submissions?period=${encodeURIComponent(period)}`),
    enabled: !!fundId && !!period,
    staleTime: 0,
  });
}

export function useFundSubmissions(fundId: string, period: string) {
  return useQuery(fundSubmissionsQuery(fundId, period));
}

export interface EmailTimelineEntry {
  id: string;
  source_type: string;
  email_received_at: string | null;
  email_sent_at: string | null;
  created_at: string;
  email_subject: string | null;
  email_body: string | null;
  email_from: string | null;
  email_to: string | null;
  email_status: string | null;
  extracted_text: string | null;
  [key: string]: unknown;
}

export function useSubmissionEmailTimeline(submissionId: string | null) {
  return useQuery({
    queryKey: ['submission-email-timeline', submissionId],
    queryFn: () => apiFetch<EmailTimelineEntry[]>(`/submissions/${encodeURIComponent(submissionId!)}/email-timeline`),
    enabled: !!submissionId,
  });
}

export function useFundPeriods(fundId: string) {
  return useQuery({
    queryKey: ['fund-periods', fundId],
    queryFn: () => apiFetch<string[]>(`/funds/${encodeURIComponent(fundId)}/periods`),
    enabled: !!fundId,
  });
}

/**
 * Hook to send quarterly email to portfolio company
 * Creates submission and sends initial metric request email
 */
export function useSendQuarterlyEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      portfolioCompanyId,
      tenantId,
      isTrial = false
    }: {
      portfolioCompanyId: string;
      tenantId: string;
      isTrial?: boolean;
    }) => apiFetch<{ period: string; recipients: { email: string }[] }>('/api/emails/send-quarterly-email', {
      method: 'POST',
      body: JSON.stringify({
        portfolio_company_id: portfolioCompanyId,
        tenant_id: tenantId,
        is_trial: isTrial,
      }),
    }),

    onSuccess: () => {
      // Invalidate submissions queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      queryClient.invalidateQueries({ queryKey: ['fund-submissions'] });
    },
  });
}
